import React from "react";

import { Box, CircularProgress, Dialog, LinearProgress, Stack, Typography } from "@mui/material";

import { Canvas } from "./components/Canvas/Canvas";
import { HeaderActions } from "./components/HeaderActions";
import { LeftSidebar } from "./components/LeftSidebar";
import { PreviewMode } from "./components/PreviewMode";
import { SettingsPanel } from "./components/SettingsPanel/SettingsPanel";
import { TopBar } from "./components/TopBar";
import { ReportGeneratorProvider, useReportGeneratorContext } from "./context/ReportGenerator-context";

const ReportGeneratorContent: React.FC = () => {
  const ctx = useReportGeneratorContext();

  if (ctx.previewMode) {
    return (
      <PreviewMode
        ref={ctx.previewRef}
        cards={ctx.cards}
        pages={ctx.pages}
        reportMeta={ctx.reportMeta}
        period={ctx.period}
        currency={ctx.currency}
        orientation={ctx.orientation}
        filters={ctx.filters}
        expandedRows={ctx.expandedRows}
        onToggleExpand={ctx.handleToggleExpand}
        autoExport={ctx.autoExport}
        onBack={() => {
          ctx.setPreviewMode(false);
          ctx.setAutoExport(false);
        }}
        onExportPDF={ctx.exportPreviewPdf}
      />
    );
  }

  const isBatchExporting = Boolean(ctx.batchExportProgress);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        fontFamily: "'Roboto', 'Segoe UI', sans-serif"
      }}
    >
      <HeaderActions
        onPreview={() => ctx.setPreviewMode(true)}
        onExportPDF={() => {
          ctx.setAutoExport(false);
          ctx.setPreviewMode(true);
        }}
        onBatchExport={ctx.handleStartBatchExport}
      />

      <TopBar
        dateRange={ctx.dateRange}
        onDateRangeChange={ctx.onDateRangeChange}
        filters={ctx.filters}
        onFiltersChange={ctx.setFilters}
      />

      <Box sx={{ display: "flex", flex: 1, overflow: "hidden", gap: "10px", bgcolor: "#f6f8fc" }}>
        <LeftSidebar
          views={ctx.views}
          activeViewId={ctx.activeViewId}
          activeTab={ctx.sidebarTab}
          hasCards={ctx.cards.length > 0}
          onTabChange={ctx.setSidebarTab}
          onLoadView={ctx.loadView}
          onDeleteView={ctx.deleteView}
          onSaveView={ctx.saveView}
          onUpdateView={ctx.updateView}
          onAutoClean={ctx.autoCleanLayout}
          onLoadLayout={ctx.onLoadLayout}
          onClearCanvas={ctx.onClearCanvas}
          setDroppingItem={ctx.setDroppingItem}
          backgroundUrl={ctx.reportMeta.background ?? ""}
          backgroundOpacity={ctx.reportMeta.backgroundOpacity ?? 100}
          onBackgroundApply={(url, opacity, file) => {
            ctx.setReportMeta((cur) => ({ ...cur, background: url, backgroundOpacity: opacity }));
            ctx.setBackgroundFile(file);
          }}
          onBackgroundRemove={() => {
            ctx.setReportMeta((cur) => ({ ...cur, background: "", backgroundOpacity: 100 }));
            ctx.setBackgroundFile(null);
          }}
        />

        <Canvas
          cards={ctx.cards}
          pages={ctx.pages}
          selectedCardId={ctx.selectedCardId}
          reportMeta={ctx.reportMeta}
          period={ctx.period}
          currency={ctx.currency}
          filters={ctx.filters}
          orientation={ctx.orientation}
          onOrientationChange={ctx.setOrientation}
          canUndo={ctx.canUndo}
          canRedo={ctx.canRedo}
          onUndo={ctx.undo}
          onRedo={ctx.redo}
          onZoomChange={ctx.setZoom}
          isResizing={ctx.isResizing}
          droppingItem={ctx.droppingItem}
          onResizingChange={ctx.setIsResizing}
          onUpdateMeta={(updates) => ctx.setReportMeta((cur) => ({ ...cur, ...updates }))}
          onLogoFileChange={ctx.setLogoFile}
          onSelectCard={ctx.setSelectedCardId}
          onRemoveCard={ctx.removeCard}
          onDuplicateCard={ctx.duplicateCard}
          onUpdateCard={ctx.updateCard}
          onPresetChange={ctx.onPresetChange}
          expandedRows={ctx.expandedRows}
          onToggleExpand={ctx.handleToggleExpand}
          onAddPage={ctx.addPage}
          onRemovePage={ctx.removePage}
          onUpdatePage={ctx.updatePage}
          onLayoutChange={ctx.onLayoutChange}
          onDropCard={ctx.onDropCard}
          onOpenSettings={ctx.openSettings}
          zoom={ctx.zoom}
        />

        {ctx.selectedCard && ctx.settingsOpen && (
          <SettingsPanel
            card={ctx.selectedCard}
            onUpdateConfig={(config) => ctx.updateCard(ctx.selectedCard!.id, { config })}
            onUpdateCardFilters={(cardFilters) => {
              const currentConfig = ctx.selectedCard!.config;
              ctx.updateCard(ctx.selectedCard!.id, {
                config: { ...currentConfig, cardFilters }
              });
            }}
            onClose={() => ctx.setSettingsOpen(false)}
          />
        )}
      </Box>

      {isBatchExporting && (
        <>
          <Box
            sx={{
              position: "fixed",
              left: -9999,
              top: 0,
              width: "auto",
              height: "auto",
              overflow: "hidden",
              pointerEvents: "none"
            }}
          >
            <PreviewMode
              ref={ctx.batchPreviewRef}
              cards={ctx.batchCards}
              pages={ctx.pages}
              reportMeta={ctx.reportMeta}
              period={ctx.period}
              currency={ctx.currency}
              orientation={ctx.orientation}
              filters={ctx.filters}
              expandedRows={ctx.expandedRows}
              onToggleExpand={ctx.handleToggleExpand}
              autoExport
              onBack={() => {}}
              onExportPDF={async () => {}}
            />
          </Box>
          <BatchProgressOverlay
            current={ctx.batchExportProgress!.current}
            total={ctx.batchExportProgress!.total}
            investor={ctx.batchExportProgress!.investor}
          />
        </>
      )}
    </Box>
  );
};

export const ReportGenerator: React.FC = () => {
  return (
    <ReportGeneratorProvider>
      <ReportGeneratorContent />
    </ReportGeneratorProvider>
  );
};

// ─── Batch export dialogs ─────────────────────────────────────────────────────

interface BatchProgressOverlayProps {
  current: number;
  total: number;
  investor: string;
}

const BatchProgressOverlay: React.FC<BatchProgressOverlayProps> = ({ current, total, investor }) => {
  const pct = Math.min(100, Math.round((current / total) * 100));

  return (
    <Dialog open maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px", p: "32px 36px" } }}>
      <Stack alignItems="center" spacing="14px">
        {/* Indeterminate spinner: continuous motion reassures the window hasn't frozen. */}
        <CircularProgress size={40} thickness={4} sx={{ color: "#071f35" }} />

        <Stack alignItems="center" spacing="4px" sx={{ width: "100%" }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#071f35" }}>Generating reports…</Typography>
          <Typography sx={{ fontSize: 13, color: "#496682" }}>
            {current} of {total}
          </Typography>
          <Typography
            noWrap
            title={investor}
            sx={{ fontSize: 13, fontWeight: 600, color: "#071f35", maxWidth: "100%" }}
          >
            {investor}
          </Typography>
        </Stack>

        <Stack sx={{ width: "100%" }} spacing="6px">
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 6,
              borderRadius: "999px",
              bgcolor: "#eef2f7",
              "& .MuiLinearProgress-bar": {
                bgcolor: "#071f35",
                borderRadius: "999px",
                transition: "transform 0.4s ease"
              }
            }}
          />
          <Typography sx={{ fontSize: 12, color: "#92a7bc", textAlign: "right" }}>{pct}%</Typography>
        </Stack>
      </Stack>
    </Dialog>
  );
};
